import { Form, FormInstance } from 'antd';
import { Store } from 'antd/lib/form/interface';
import moment from 'moment';
import React, { useMemo } from 'react';

import { useMutation } from '@common/hooks';
import {
  ComplianceItemFilesListV2,
  ComplianceItemInputExpirationDate,
} from '@onboarding/components';
import { ComplianceItemV2Type } from '@onboarding/graphql';
import { UpdateNurseCertificationMutation } from 'works/legacy/graphql/nurse-certification';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemV2Type;
  refetchQueries: string[];
  onDeleted?: () => void;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemUpdateFormCertification({
  form,
  complianceItem,
  refetchQueries,
  onDeleted,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update certification. Please try again.');

  const [updateNurseCertification, { loading: formSubmitting }] = useMutation(
    UpdateNurseCertificationMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateNurseCertification.errors) {
          addErrorsToForm(form, response.updateNurseCertification.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage(
            'success',
            'Certification has been successfully updated.'
          );
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const initialValues = useMemo(
    () => ({
      completedOn: detailItem.completedOn && moment(detailItem.completedOn),
      expiresOn: detailItem.expiresOn && moment(detailItem.expiresOn),
    }),
    [detailItem]
  );

  if (!detailItem) {
    return null;
  }

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    updateNurseCertification({
      variables: {
        id: complianceItem.id,
        certificationId: detailItem.certificationUuid,
        files: values.files?.fileList ?? [],
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}
    >
      <ComplianceItemFilesListV2
        refetchQueries={refetchQueries}
        complianceItemId={complianceItem.id}
        complianceItemStateApprovedAt={complianceItem.stateApprovedAt}
        attachmentList={detailItem.detailItemAttachments}
        onDeleted={onDeleted}
        formContent={
          <>
            {detailItem.name && (
              <Form.Item label="Certification">{detailItem.name}</Form.Item>
            )}

            <ComplianceItemInputExpirationDate
              expirationType={complianceItem.expirationType}
            />
          </>
        }
      />
    </Form>
  );
}
